import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
  Search, Send, User, Phone, BookOpen, Users,
  MessageCircle, FileText, AlertTriangle, CheckCircle,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent,
  EmptyState,
} from "../ui";
import apiClient from "../../utils/apiClient";
import "./send-message-to-student.scss";

/* ── Helpers to extract template parameters ────────────────────────────────── */

/**
 * Extract parameters from a template component.
 * Supports both named params ({{param_name}}) and positional params ({{1}}).
 * Returns: [{ key, label, example }]
 */
function extractParamsFromComponent(comp) {
  if (!comp?.text) return [];
  const params = [];
  // Match {{param_name}} or {{1}}, {{2}} etc.
  const regex = /\{\{([^}]+)\}\}/g;
  let match;
  const seen = new Set();
  while ((match = regex.exec(comp.text)) !== null) {
    const raw = match[1].trim();
    if (seen.has(raw)) continue;
    seen.add(raw);

    // Find example value
    let example = "";
    if (comp.example?.body_text_named_params) {
      const np = comp.example.body_text_named_params.find(p => p.param_name === raw);
      if (np) example = np.example || "";
    } else if (comp.example?.body_text?.[0]) {
      const idx = parseInt(raw, 10);
      if (!isNaN(idx) && idx >= 1) {
        example = comp.example.body_text[0][idx - 1] || "";
      }
    } else if (comp.example?.header_text) {
      const idx = parseInt(raw, 10);
      if (!isNaN(idx) && idx >= 1) {
        example = comp.example.header_text[idx - 1] || "";
      }
    }

    const isPositional = /^\d+$/.test(raw);
    params.push({
      key: raw,
      label: isPositional ? `Parameter ${raw}` : raw.replace(/_/g, " "),
      example,
    });
  }
  return params;
}

/**
 * Extract button URL params from BUTTONS component.
 */
function extractButtonParams(comp) {
  if (comp?.type !== "BUTTONS" || !comp.buttons) return [];
  const params = [];
  comp.buttons.forEach((btn, btnIdx) => {
    if (btn.type === "URL" && btn.url) {
      const regex = /\{\{(\d+)\}\}/g;
      let match;
      while ((match = regex.exec(btn.url)) !== null) {
        const raw = match[1].trim();
        let example = "";
        if (btn.example?.[0]) example = btn.example[0];
        params.push({
          key: `btn_${btnIdx}_${raw}`,
          label: `Button "${btn.text}" URL parameter`,
          example,
          buttonIndex: btnIdx,
        });
      }
    }
  });
  return params;
}

/**
 * Given a template object, extract all variable slots grouped by component type.
 */
function extractTemplateVariables(template) {
  if (!template?.components) return { header: [], body: [], button: [] };

  const headerComp = template.components.find(c => c.type === "HEADER");
  const bodyComp = template.components.find(c => c.type === "BODY");
  const buttonsComp = template.components.find(c => c.type === "BUTTONS");

  return {
    header: extractParamsFromComponent(headerComp),
    body: extractParamsFromComponent(bodyComp),
    button: extractButtonParams(buttonsComp),
  };
}

/**
 * Build the Meta API components array from filled variable values.
 */
function _buildParam(p, values, prefix) {
  const isPositional = /^\d+$/.test(p.key);
  const param = { type: "text", text: values[`${prefix}_${p.key}`] || "" };
  if (!isPositional) param.parameter_name = p.key;
  return param;
}

function buildComponents(vars, values) {
  const components = [];

  if (vars.header.length > 0) {
    components.push({
      type: "header",
      parameters: vars.header.map(p => _buildParam(p, values, "header")),
    });
  }

  if (vars.body.length > 0) {
    components.push({
      type: "body",
      parameters: vars.body.map(p => _buildParam(p, values, "body")),
    });
  }

  if (vars.button.length > 0) {
    const byBtn = {};
    vars.button.forEach(p => {
      if (!byBtn[p.buttonIndex]) byBtn[p.buttonIndex] = [];
      byBtn[p.buttonIndex].push(p);
    });
    Object.entries(byBtn).forEach(([idx, params]) => {
      components.push({
        type: "button",
        sub_type: "url",
        index: Number.parseInt(idx, 10),
        parameters: params.map(p => _buildParam(p, values, "button")),
      });
    });
  }

  return components;
}

/**
 * Replace {{param}} placeholders in text with actual values for preview.
 */
function fillPreviewText(text, params, values, prefix) {
  if (!text) return text;
  let result = text;
  params.forEach(p => {
    const val = values[`${prefix}_${p.key}`];
    const display = val || `{{${p.key}}}`;
    result = result.replace(`{{${p.key}}}`, display);
  });
  return result;
}

/* ══════════════════════════════════════════════════════════════════════════════ */

const SendMessageToStudent = ({ showToast }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // External phone number mode
  const [useExternalPhone, setUseExternalPhone] = useState(false);
  const [externalPhone, setExternalPhone] = useState("");

  // Message form
  const [messageType, setMessageType] = useState("template");
  const [text, setText] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templates, setTemplates] = useState([]);
  const [paramValues, setParamValues] = useState({});
  const [sending, setSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);

  const searchTimeout = useRef(null);

  // Selected template object
  const selectedTemplate = useMemo(
    () => templates.find(t => t.name === templateName) || null,
    [templates, templateName],
  );

  // Extracted variables for selected template
  const templateVars = useMemo(
    () => (selectedTemplate ? extractTemplateVariables(selectedTemplate) : { header: [], body: [], button: [] }),
    [selectedTemplate],
  );

  const totalParams = templateVars.header.length + templateVars.body.length + templateVars.button.length;

  // Load approved templates once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/whatsapp/templates");
        setTemplates((data.templates || []).filter(t => t.status === "APPROVED"));
      } catch { /* ignore */ }
    })();
  }, []);

  // Reset param values when template changes
  useEffect(() => {
    setParamValues({});
  }, [templateName]);

  // Debounced search
  const handleSearch = useCallback((value) => {
    setQuery(value);
    setSentSuccess(false);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (value.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(
          `/whatsapp/students/search?q=${encodeURIComponent(value.trim())}`
        );
        setResults(data.students || []);
      } catch {
        setResults([]);
      }
      setSearching(false);
    }, 350);
  }, []);

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setQuery("");
    setResults([]);
    setText("");
    setTemplateName("");
    setParamValues({});
    setSentSuccess(false);
  };

  const handleClearStudent = () => {
    setSelectedStudent(null);
    setExternalPhone("");
    setUseExternalPhone(false);
    setText("");
    setTemplateName("");
    setParamValues({});
    setSentSuccess(false);
  };

  const handleParamChange = (key, value) => {
    setParamValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSend = async () => {
    if (!selectedStudent && !externalPhone.trim()) return;
    setSending(true);
    setSentSuccess(false);
    try {
      const payload = {
        messageType,
      };
      if (selectedStudent) {
        payload.studentId = selectedStudent._id;
      } else {
        payload.toPhone = externalPhone.trim();
      }
      if (messageType === "template") {
        payload.templateName = templateName;
        payload.languageCode = selectedTemplate?.language || "en_US";
        if (totalParams > 0) {
          payload.components = buildComponents(templateVars, paramValues);
        }
      } else {
        payload.text = text.trim();
      }
      const { data } = await apiClient.post("/whatsapp/send-message", payload);
      showToast(data.message || "Message sent successfully");
      setSentSuccess(true);
      setText("");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to send message", "error");
    }
    setSending(false);
  };

  // Validate all required params are filled
  const allParamsFilled = totalParams === 0 || [
    ...templateVars.header.map(p => `header_${p.key}`),
    ...templateVars.body.map(p => `body_${p.key}`),
    ...templateVars.button.map(p => `button_${p.key}`),
  ].every(k => paramValues[k]?.trim());

  const hasRecipient = selectedStudent || (useExternalPhone && externalPhone.trim().length >= 10);

  const canSend = hasRecipient && (
    (messageType === "template" && templateName && allParamsFilled) ||
    (messageType === "text" && text.trim())
  );

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle><Send size={18} /> Send Message</AppCardTitle>
        <p className="settings-card-desc">
          Send a WhatsApp message to a student or any phone number
        </p>
      </AppCardHeader>
      <AppCardContent>
        {/* ── Step 1: Recipient Selection ─────────────────────────── */}
        {!selectedStudent && !useExternalPhone ? (
          <div className="wa-send-search-section">
            {/* Toggle: Student vs External Phone */}
            <div className="wa-send-type-toggle" style={{ marginBottom: 12 }}>
              <button
                className="wa-send-type-btn active"
                onClick={() => {}}
              >
                <User size={14} /> Search Student
              </button>
              <button
                className="wa-send-type-btn"
                onClick={() => { setUseExternalPhone(true); setQuery(""); setResults([]); }}
              >
                <Phone size={14} /> Enter Phone Number
              </button>
            </div>

            <div className="wa-send-search-box">
              <Search size={16} className="wa-send-search-icon" />
              <input
                type="text"
                className="wa-send-search-input"
                placeholder="Search student by name or mobile number..."
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
              />
              {searching && <Spinner animation="border" size="sm" className="wa-send-search-spinner" />}
            </div>

            {results.length > 0 && (
              <div className="wa-send-results">
                {results.map((s) => (
                  <div
                    key={s._id}
                    className="wa-send-result-item"
                    onClick={() => handleSelectStudent(s)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleSelectStudent(s)}
                  >
                    <div className="wa-send-result-avatar">
                      <User size={18} />
                    </div>
                    <div className="wa-send-result-info">
                      <div className="wa-send-result-name">{s.name}</div>
                      <div className="wa-send-result-meta">
                        <span><Phone size={12} /> {s.mobile}</span>
                        {s.course?.name && <span><BookOpen size={12} /> {s.course.name}</span>}
                        {s.batch?.name && <span><Users size={12} /> {s.batch.name}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {query.length >= 2 && !searching && results.length === 0 && (
              <div className="wa-send-no-results">
                No students found matching "{query}"
              </div>
            )}

            {!query && (
              <EmptyState
                icon={<Search size={32} />}
                title="Search for a Recipient"
                description="Type a student name or mobile number above, or switch to 'Enter Phone Number' to message any number."
              />
            )}
          </div>
        ) : !selectedStudent && useExternalPhone ? (
          /* ── External Phone Number Entry ──────────────────────────── */
          <div className="wa-send-search-section">
            <div className="wa-send-type-toggle" style={{ marginBottom: 12 }}>
              <button
                className="wa-send-type-btn"
                onClick={() => { setUseExternalPhone(false); setExternalPhone(""); }}
              >
                <User size={14} /> Search Student
              </button>
              <button
                className="wa-send-type-btn active"
                onClick={() => {}}
              >
                <Phone size={14} /> Enter Phone Number
              </button>
            </div>

            <div className="wa-send-selected" style={{ marginBottom: 12 }}>
              <div className="wa-send-selected-avatar">
                <Phone size={22} />
              </div>
              <div className="wa-send-selected-info" style={{ flex: 1 }}>
                <Form.Control
                  type="tel"
                  value={externalPhone}
                  onChange={(e) => setExternalPhone(e.target.value)}
                  placeholder="Enter phone number with country code (e.g. 919876543210)"
                  autoFocus
                />
                <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary, #9ca3af)", marginTop: 4 }}>
                  Enter the full number including country code. For India: 91 + 10-digit number
                </div>
              </div>
            </div>

            {/* ── Compose Message (same as selected student) ───────── */}
            <div className="wa-send-compose">
              <div className="wa-send-type-toggle">
                <button
                  className={`wa-send-type-btn ${messageType === "template" ? "active" : ""}`}
                  onClick={() => setMessageType("template")}
                >
                  <FileText size={14} /> Template Message
                </button>
                <button
                  className={`wa-send-type-btn ${messageType === "text" ? "active" : ""}`}
                  onClick={() => setMessageType("text")}
                >
                  <MessageCircle size={14} /> Free Text
                </button>
              </div>

              {messageType === "template" ? (
                <div className="wa-send-template-section">
                  <Form.Group className="mb-3">
                    <Form.Label>Template</Form.Label>
                    <Form.Select
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                    >
                      <option value="">Select a template...</option>
                      {templates.map((t) => (
                        <option key={t._id} value={t.name}>
                          {t.name} ({t.language}) — {t.category}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {selectedTemplate && totalParams > 0 && (
                    <div className="wa-send-vars">
                      <div className="wa-send-vars-label">
                        Template Variables ({totalParams})
                      </div>
                      {templateVars.header.length > 0 && (
                        <div className="wa-send-vars-group">
                          <div className="wa-send-vars-group-label">Header</div>
                          {templateVars.header.map((p) => (
                            <Form.Group key={`header_${p.key}`} className="mb-2">
                              <Form.Label className="wa-send-var-label">{p.label}</Form.Label>
                              <Form.Control
                                size="sm"
                                value={paramValues[`header_${p.key}`] || ""}
                                onChange={(e) => handleParamChange(`header_${p.key}`, e.target.value)}
                                placeholder={p.example || `Enter ${p.label}...`}
                              />
                            </Form.Group>
                          ))}
                        </div>
                      )}
                      {templateVars.body.length > 0 && (
                        <div className="wa-send-vars-group">
                          <div className="wa-send-vars-group-label">Body</div>
                          {templateVars.body.map((p) => (
                            <Form.Group key={`body_${p.key}`} className="mb-2">
                              <Form.Label className="wa-send-var-label">{p.label}</Form.Label>
                              <Form.Control
                                size="sm"
                                value={paramValues[`body_${p.key}`] || ""}
                                onChange={(e) => handleParamChange(`body_${p.key}`, e.target.value)}
                                placeholder={p.example || `Enter ${p.label}...`}
                              />
                            </Form.Group>
                          ))}
                        </div>
                      )}
                      {templateVars.button.length > 0 && (
                        <div className="wa-send-vars-group">
                          <div className="wa-send-vars-group-label">Buttons</div>
                          {templateVars.button.map((p) => (
                            <Form.Group key={`button_${p.key}`} className="mb-2">
                              <Form.Label className="wa-send-var-label">{p.label}</Form.Label>
                              <Form.Control
                                size="sm"
                                value={paramValues[`button_${p.key}`] || ""}
                                onChange={(e) => handleParamChange(`button_${p.key}`, e.target.value)}
                                placeholder={p.example || `Enter URL value...`}
                              />
                            </Form.Group>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {selectedTemplate && (
                    <TemplatePreview
                      template={selectedTemplate}
                      vars={templateVars}
                      values={paramValues}
                    />
                  )}

                  <div className="wa-send-hint">
                    <FileText size={13} />
                    Template messages can be sent anytime — no 24-hour window required.
                  </div>
                </div>
              ) : (
                <div className="wa-send-text-section">
                  <Form.Group className="mb-3">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Type your message..."
                      maxLength={4096}
                    />
                    <Form.Text className="text-muted">{text.length}/4096</Form.Text>
                  </Form.Group>
                  <div className="wa-send-hint wa-send-hint--warning">
                    <AlertTriangle size={13} />
                    Free text messages require the recipient to have messaged within the last 24 hours.
                  </div>
                </div>
              )}

              {sentSuccess && (
                <div className="wa-send-success">
                  <CheckCircle size={14} /> Message sent successfully!
                </div>
              )}

              <div className="wa-send-actions">
                <AppButton variant="outline" size="sm" onClick={handleClearStudent} style={{ marginRight: 8 }}>
                  Back
                </AppButton>
                <AppButton
                  onClick={handleSend}
                  disabled={!canSend || sending}
                >
                  {sending ? (
                    <><Spinner animation="border" size="sm" /> Sending...</>
                  ) : (
                    <><Send size={14} /> Send Message</>
                  )}
                </AppButton>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* ── Selected Student Card ────────────────────────────── */}
            <div className="wa-send-selected">
              <div className="wa-send-selected-avatar">
                <User size={22} />
              </div>
              <div className="wa-send-selected-info">
                <div className="wa-send-selected-name">{selectedStudent.name}</div>
                <div className="wa-send-selected-meta">
                  <span><Phone size={12} /> {selectedStudent.mobile}</span>
                  {selectedStudent.course?.name && (
                    <span><BookOpen size={12} /> {selectedStudent.course.name}</span>
                  )}
                  {selectedStudent.batch?.name && (
                    <span><Users size={12} /> {selectedStudent.batch.name}</span>
                  )}
                </div>
              </div>
              <AppButton variant="outline" size="sm" onClick={handleClearStudent}>
                Change
              </AppButton>
            </div>

            {/* ── Step 2: Compose Message ──────────────────────────── */}
            <div className="wa-send-compose">
              <div className="wa-send-type-toggle">
                <button
                  className={`wa-send-type-btn ${messageType === "template" ? "active" : ""}`}
                  onClick={() => setMessageType("template")}
                >
                  <FileText size={14} /> Template Message
                </button>
                <button
                  className={`wa-send-type-btn ${messageType === "text" ? "active" : ""}`}
                  onClick={() => setMessageType("text")}
                >
                  <MessageCircle size={14} /> Free Text
                </button>
              </div>

              {messageType === "template" ? (
                <div className="wa-send-template-section">
                  <Form.Group className="mb-3">
                    <Form.Label>Template</Form.Label>
                    <Form.Select
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                    >
                      <option value="">Select a template...</option>
                      {templates.map((t) => (
                        <option key={t._id} value={t.name}>
                          {t.name} ({t.language}) — {t.category}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {/* ── Variable inputs ──────────────────────────────── */}
                  {selectedTemplate && totalParams > 0 && (
                    <div className="wa-send-vars">
                      <div className="wa-send-vars-label">
                        Template Variables ({totalParams})
                      </div>

                      {templateVars.header.length > 0 && (
                        <div className="wa-send-vars-group">
                          <div className="wa-send-vars-group-label">Header</div>
                          {templateVars.header.map((p) => (
                            <Form.Group key={`header_${p.key}`} className="mb-2">
                              <Form.Label className="wa-send-var-label">{p.label}</Form.Label>
                              <Form.Control
                                size="sm"
                                value={paramValues[`header_${p.key}`] || ""}
                                onChange={(e) => handleParamChange(`header_${p.key}`, e.target.value)}
                                placeholder={p.example || `Enter ${p.label}...`}
                              />
                            </Form.Group>
                          ))}
                        </div>
                      )}

                      {templateVars.body.length > 0 && (
                        <div className="wa-send-vars-group">
                          <div className="wa-send-vars-group-label">Body</div>
                          {templateVars.body.map((p) => (
                            <Form.Group key={`body_${p.key}`} className="mb-2">
                              <Form.Label className="wa-send-var-label">{p.label}</Form.Label>
                              <Form.Control
                                size="sm"
                                value={paramValues[`body_${p.key}`] || ""}
                                onChange={(e) => handleParamChange(`body_${p.key}`, e.target.value)}
                                placeholder={p.example || `Enter ${p.label}...`}
                              />
                            </Form.Group>
                          ))}
                        </div>
                      )}

                      {templateVars.button.length > 0 && (
                        <div className="wa-send-vars-group">
                          <div className="wa-send-vars-group-label">Buttons</div>
                          {templateVars.button.map((p) => (
                            <Form.Group key={`button_${p.key}`} className="mb-2">
                              <Form.Label className="wa-send-var-label">{p.label}</Form.Label>
                              <Form.Control
                                size="sm"
                                value={paramValues[`button_${p.key}`] || ""}
                                onChange={(e) => handleParamChange(`button_${p.key}`, e.target.value)}
                                placeholder={p.example || `Enter URL value...`}
                              />
                            </Form.Group>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Live Preview ─────────────────────────────────── */}
                  {selectedTemplate && (
                    <TemplatePreview
                      template={selectedTemplate}
                      vars={templateVars}
                      values={paramValues}
                    />
                  )}

                  <div className="wa-send-hint">
                    <FileText size={13} />
                    Template messages can be sent anytime — no 24-hour window required.
                  </div>
                </div>
              ) : (
                <div className="wa-send-text-section">
                  <Form.Group className="mb-3">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Type your message..."
                      maxLength={4096}
                    />
                    <Form.Text className="text-muted">{text.length}/4096</Form.Text>
                  </Form.Group>
                  <div className="wa-send-hint wa-send-hint--warning">
                    <AlertTriangle size={13} />
                    Free text messages require the student to have messaged within the last 24 hours.
                  </div>
                </div>
              )}

              {sentSuccess && (
                <div className="wa-send-success">
                  <CheckCircle size={14} /> Message sent successfully!
                </div>
              )}

              <div className="wa-send-actions">
                <AppButton
                  onClick={handleSend}
                  disabled={!canSend || sending}
                >
                  {sending ? (
                    <><Spinner animation="border" size="sm" /> Sending...</>
                  ) : (
                    <><Send size={14} /> Send Message</>
                  )}
                </AppButton>
              </div>
            </div>
          </>
        )}
      </AppCardContent>
    </AppCard>
  );
};

/* ─── Template Preview (WhatsApp-style bubble) ─────────────────────────────── */
const TemplatePreview = ({ template, vars, values }) => {
  const headerComp = template.components?.find(c => c.type === "HEADER");
  const bodyComp = template.components?.find(c => c.type === "BODY");
  const footerComp = template.components?.find(c => c.type === "FOOTER");
  const buttonsComp = template.components?.find(c => c.type === "BUTTONS");

  const headerText = headerComp?.format === "TEXT"
    ? fillPreviewText(headerComp.text, vars.header, values, "header")
    : null;
  const bodyText = bodyComp?.text
    ? fillPreviewText(bodyComp.text, vars.body, values, "body")
    : null;
  const footerText = footerComp?.text || null;

  return (
    <div className="wa-send-preview">
      <div className="wa-send-preview-label">Preview</div>
      <div className="wa-send-preview-bubble">
        {headerText && <div className="wa-send-preview-header">{headerText}</div>}
        {bodyText && <div className="wa-send-preview-body">{bodyText}</div>}
        {footerText && <div className="wa-send-preview-footer">{footerText}</div>}
        {buttonsComp?.buttons?.length > 0 && (
          <div className="wa-send-preview-buttons">
            {buttonsComp.buttons.map((btn, i) => (
              <div key={btn.text || i} className="wa-send-preview-btn">{btn.text}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SendMessageToStudent;
